import { CSSProperties, useContext, useEffect } from 'react';
import GameContext from 'contexts/GameContext';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { useOrientation } from 'hooks/useOrientation';
import { useTableSelector } from 'hooks/useTableSelector';

import type { WaitingRegistrant } from 'components/SitGoWaiting/sitGoWaitingState';
import { seatVoteRole } from 'components/UserActions/MultiRunActions/votePresentation';

import {
  PortraitCurrentPlayer,
  PortraitOpponent,
  LandscapeCurrentPlayer,
  LandscapeOpponent,
  OpenSeat,
  WaitingSeat,
  useSeatLogic,
  useCardPosition
} from './modes';

type SeatType = {
  seat: any;
  index: number;
  guid: any;
  isFaceCardUp: boolean;
  position: SeatPosition;
  currentSeatPositionLength: number;
  currentPlayerSeatId: number;
  seatId: number;
  cards: any;
  seatPositionsLength: number;
  isRotating?: boolean;
  /**
   * EN-11257: the Sit & Go registrant drawn into this empty seat while the
   * table fills (release.sit-go-seated-waiting on). Ignored once the seat has
   * a real occupant.
   */
  waitingRegistrant?: WaitingRegistrant;
};

// Carries the seat's multi-run vote role (EN-10856) for the felt state's CSS,
// without adding a box: every seat mode positions itself against the table
// root, and `display: contents` leaves that untouched. Always rendered, so a
// vote opening or closing never remounts the seat underneath it.
const VOTE_ROLE_WRAPPER: CSSProperties = { display: 'contents' };

export const SeatContainer = ({
  seat,
  isFaceCardUp,
  position,
  currentPlayerSeatId,
  seatId,
  cards,
  isRotating = false,
  waitingRegistrant
}: SeatType) => {
  const { setIsPlayerReady } = useContext(GameContext);
  const game = useTableSelector((table) => table.game);
  const { isOmaha } = game;
  const holeCardCount = isOmaha ? 4 : 2;

  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const isCurrentPlayer = isFaceCardUp && seat.ID;
  const isHandHistorySnapshot = useHandHistorySnapshotMode();

  const { isTurn, playerAction, displayAction, hasAction, actionColor, isWinner } =
    useSeatLogic(seatId);
  const cardPosition = useCardPosition(position);

  useEffect(() => {
    if (position) {
      setIsPlayerReady(true);
    }
  }, [position, setIsPlayerReady]);

  if (!position) return null;

  // Open seat
  if (!seat.ID) {
    // A Sit & Go registrant waiting for the table to fill: drawn like an
    // occupied opponent seat, but never part of a recorded hand.
    if (waitingRegistrant && !isHandHistorySnapshot) {
      return (
        <WaitingSeat
          registrant={waitingRegistrant}
          seatId={seatId}
          currentPlayerSeatId={currentPlayerSeatId}
          position={position}
          cardPosition={cardPosition}
          holeCardCount={holeCardCount}
          isRotating={isRotating}
        />
      );
    }
    // A hand-history snapshot draws the table as it was, empty seats included,
    // but nobody can sit at a recorded hand: the seat is inert there.
    return (
      <OpenSeat
        seatId={seatId}
        position={position}
        isRotating={isRotating}
        inert={isHandHistorySnapshot}
      />
    );
  }

  // Shared props for all seat components
  const commonProps = {
    seat,
    seatId,
    currentPlayerSeatId,
    isFaceCardUp,
    position,
    cards,
    hasAction,
    playerAction,
    displayAction,
    actionColor,
    isWinner,
    isTurn,
    cardPosition,
    isRotating
  };

  let seatMode;
  if (isPortrait) {
    seatMode = isCurrentPlayer ? (
      <PortraitCurrentPlayer {...commonProps} holeCardCount={holeCardCount} />
    ) : (
      <PortraitOpponent {...commonProps} holeCardCount={holeCardCount} />
    );
  } else {
    seatMode = isCurrentPlayer ? (
      <LandscapeCurrentPlayer {...commonProps} />
    ) : (
      <LandscapeOpponent {...commonProps} holeCardCount={holeCardCount} />
    );
  }

  return (
    <div data-vote-role={seatVoteRole(game, seatId)} style={VOTE_ROLE_WRAPPER}>
      {seatMode}
    </div>
  );
};
